import React, { useState, useEffect, useCallback } from 'react';
import { 
  getProductDetails, 
  uploadProductDetail, 
  deleteProductDetail, 
  downloadProductDetail 
} from '../services/productDetailsService';
import { logger } from '../utils/logger';
import './ProductDetails.css';

const DOCUMENT_TYPES = [
  'Product Overview',
  'Technical Specs',
  'User Guide',
  'API Documentation',
  'Glossary'
];

const MAX_FILE_SIZE = 512000; // 500KB in bytes

const ProductDetails = ({ featureGroupId, userRole }) => {
  const [productDetails, setProductDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'paste'
  const [selectedFile, setSelectedFile] = useState(null);
  const [pasteContent, setPasteContent] = useState('');
  const [viewingContent, setViewingContent] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewTab, setViewTab] = useState('text'); // 'text' or 'formatted'

  const isOwnerOrEditor = userRole === 'Owner' || userRole === 'Editor';

  const loadProductDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProductDetails(featureGroupId);
      setProductDetails(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load product details');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId]);

  useEffect(() => {
    if (featureGroupId) {
      loadProductDetails();
    }
  }, [featureGroupId, loadProductDetails]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getProductDetailForType = (documentType) => {
    return productDetails.find(pd => pd.document_type === documentType);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`);
      event.target.value = '';
      return;
    }

    // Validate file type
    const validTypes = ['text/markdown', 'text/plain', 'application/octet-stream'];
    const validExtensions = ['.md', '.txt'];
    const fileName = file.name.toLowerCase();
    const isValidType = validTypes.includes(file.type) || 
                       validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidType) {
      setError('Invalid file type. Please upload a .md or .txt file.');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handlePasteChange = (event) => {
    const content = event.target.value;
    setPasteContent(content);
    
    // Calculate size
    const size = new TextEncoder().encode(content).length;
    if (size > MAX_FILE_SIZE) {
      setError(`Content size (${formatFileSize(size)}) exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`);
    } else {
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedType) {
      setError('Please select a document type');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      let fileContent;
      let fileName;

      if (uploadMethod === 'file') {
        if (!selectedFile) {
          setError('Please select a file');
          setUploading(false);
          return;
        }
        fileContent = selectedFile;
        fileName = selectedFile.name;
      } else {
        // Paste method
        if (!pasteContent.trim()) {
          setError('Please paste or enter markdown content');
          setUploading(false);
          return;
        }
        const size = new TextEncoder().encode(pasteContent).length;
        if (size > MAX_FILE_SIZE) {
          setError(`Content size (${formatFileSize(size)}) exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`);
          setUploading(false);
          return;
        }
        fileContent = pasteContent;
        fileName = `${selectedType}.md`;
      }

      // Simulate progress (Supabase doesn't provide upload progress events)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      await uploadProductDetail(featureGroupId, selectedType, fileContent, fileName);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Reset form
      setSelectedFile(null);
      setPasteContent('');
      setShowUploadDialog(false);
      setSelectedType(null);
      setUploadMethod('file');

      // Reload list
      await loadProductDetails();

      // Reset progress after a moment
      setTimeout(() => setUploadProgress(0), 500);
    } catch (err) {
      setError(err.message || 'Failed to upload product detail');
      logger.error(err);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, documentType) => {
    if (!window.confirm(`Are you sure you want to delete the "${documentType}" document? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deleteProductDetail(id);
      await loadProductDetails();
    } catch (err) {
      setError(err.message || 'Failed to delete product detail');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id, documentType, fileName) => {
    try {
      setLoading(true);
      setError(null);
      const { content, fileName: downloadFileName } = await downloadProductDetail(id);
      
      // Create download link
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName || `${documentType}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download product detail');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { content } = await downloadProductDetail(id);
      setViewingContent(content);
      setViewTab('text'); // Reset to text tab when opening
      setShowViewDialog(true);
    } catch (err) {
      setError(err.message || 'Failed to load product detail');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Simple markdown to HTML converter
  const markdownToHTML = (markdown) => {
    if (!markdown) return '';
    
    const escapeHTML = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    
    // Process code blocks first (to preserve them)
    const codeBlocks = [];
    let codeBlockIndex = 0;
    let html = markdown.replace(/```(\w+)?\n?([\s\S]*?)```/g, (match, lang, code) => {
      const placeholder = `__CODE_BLOCK_${codeBlockIndex}__`;
      codeBlocks[codeBlockIndex] = `<pre><code>${escapeHTML(code.trim())}</code></pre>`;
      codeBlockIndex++;
      return placeholder;
    });
    
    // Process line by line
    const lines = html.split('\n');
    const processed = [];
    let inList = false;
    let listItems = [];
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      const trimmed = line.trim();
      
      // Check for code block placeholder
      if (trimmed.startsWith('__CODE_BLOCK_')) {
        const index = parseInt(trimmed.match(/\d+/)[0]);
        if (inList) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push(codeBlocks[index]);
        continue;
      }
      
      // Headers
      if (trimmed.match(/^### /)) {
        if (inList) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push(`<h3>${escapeHTML(trimmed.substring(4))}</h3>`);
        continue;
      }
      if (trimmed.match(/^## /)) {
        if (inList) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push(`<h2>${escapeHTML(trimmed.substring(3))}</h2>`);
        continue;
      }
      if (trimmed.match(/^# /)) {
        if (inList) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push(`<h1>${escapeHTML(trimmed.substring(2))}</h1>`);
        continue;
      }
      
      // Horizontal rules
      if (trimmed === '---' || trimmed === '***') {
        if (inList) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push('<hr />');
        continue;
      }
      
      // Blockquotes
      if (trimmed.startsWith('> ')) {
        if (inList) {
          processed.push('</ul>');
          inList = false;
        }
        processed.push(`<blockquote>${escapeHTML(trimmed.substring(2))}</blockquote>`);
        continue;
      }
      
      // Lists
      if (trimmed.match(/^[*-] /) || trimmed.match(/^\d+\. /)) {
        const content = trimmed.replace(/^[*-] /, '').replace(/^\d+\. /, '');
        if (!inList) {
          processed.push('<ul>');
          inList = true;
        }
        listItems.push(`<li>${escapeHTML(content)}</li>`);
        continue;
      }
      
      // End list if we hit a non-list line
      if (inList && trimmed) {
        processed.push(...listItems);
        processed.push('</ul>');
        listItems = [];
        inList = false;
      }
      
      // Regular paragraph
      if (trimmed) {
        let paraContent = escapeHTML(trimmed);
        
        // Process inline markdown (only if not in code)
        // Bold
        paraContent = paraContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        paraContent = paraContent.replace(/__([^_]+)__/g, '<strong>$1</strong>');
        
        // Italic
        paraContent = paraContent.replace(/\*([^*]+)\*/g, '<em>$1</em>');
        paraContent = paraContent.replace(/_([^_]+)_/g, '<em>$1</em>');
        
        // Inline code
        paraContent = paraContent.replace(/`([^`]+)`/g, '<code>$1</code>');
        
        // Links
        paraContent = paraContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        
        // Images
        paraContent = paraContent.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');
        
        processed.push(`<p>${paraContent}</p>`);
      }
    }
    
    // Close any open list
    if (inList) {
      processed.push(...listItems);
      processed.push('</ul>');
    }
    
    return processed.join('\n');
  };

  const handleOpenUpload = (documentType) => {
    setSelectedType(documentType);
    setSelectedFile(null);
    setPasteContent('');
    setUploadMethod('file');
    setError(null);
    setShowUploadDialog(true);
  };

  const handleReplace = (documentType) => {
    handleOpenUpload(documentType);
  };

  return (
    <div className="product-details-container">
      <div className="product-details-header">
        <h3>Product Details</h3>
        <p className="product-details-description">
          Upload context documents that can be used in AI prompts. One document per type per feature map.
        </p>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {loading && productDetails.length === 0 ? (
        <div className="loading">Loading product details...</div>
      ) : (
        <div className="product-details-list">
          {DOCUMENT_TYPES.map((docType) => {
            const productDetail = getProductDetailForType(docType);
            const isUploaded = !!productDetail;

            return (
              <div key={docType} className="product-detail-item">
                <div className="product-detail-header">
                  <div className="product-detail-info">
                    <h4>{docType}</h4>
                    {isUploaded ? (
                      <div className="product-detail-meta">
                        <span className="file-info">
                          <i className="fas fa-file"></i> {productDetail.file_name}
                        </span>
                        <span className="file-size">
                          {formatFileSize(productDetail.file_size)}
                        </span>
                        <span className="updated-date">
                          Updated: {formatDate(productDetail.updated_at)}
                        </span>
                      </div>
                    ) : (
                      <span className="not-uploaded">Not uploaded</span>
                    )}
                  </div>
                  <div className="product-detail-actions">
                    {isUploaded ? (
                      <>
                        <button
                          type="button"
                          className="action-button view-button"
                          onClick={() => handleView(productDetail.id)}
                          disabled={loading}
                          title="View document"
                        >
                          <i className="fas fa-eye"></i> View
                        </button>
                        <button
                          type="button"
                          className="action-button download-button"
                          onClick={() => handleDownload(productDetail.id, docType, productDetail.file_name)}
                          disabled={loading}
                          title="Download document"
                        >
                          <i className="fas fa-download"></i> Download
                        </button>
                        {isOwnerOrEditor && (
                          <>
                            <button
                              type="button"
                              className="action-button replace-button"
                              onClick={() => handleReplace(docType)}
                              disabled={loading || uploading}
                              title="Replace document"
                            >
                              <i className="fas fa-upload"></i> Replace
                            </button>
                            <button
                              type="button"
                              className="action-button delete-button"
                              onClick={() => handleDelete(productDetail.id, docType)}
                              disabled={loading}
                              title="Delete document"
                            >
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      isOwnerOrEditor && (
                        <button
                          type="button"
                          className="action-button upload-button"
                          onClick={() => handleOpenUpload(docType)}
                          disabled={loading || uploading}
                          title="Upload document"
                        >
                          <i className="fas fa-upload"></i> Upload
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      {showUploadDialog && (
        <div className="upload-dialog-overlay" onClick={() => !uploading && setShowUploadDialog(false)}>
          <div className="upload-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="upload-dialog-header">
              <h3>Upload {selectedType}</h3>
              <button
                type="button"
                className="close-button"
                onClick={() => !uploading && setShowUploadDialog(false)}
                disabled={uploading}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <div className="upload-dialog-content">
              <div className="upload-method-selector">
                <label>
                  <input
                    type="radio"
                    value="file"
                    checked={uploadMethod === 'file'}
                    onChange={(e) => {
                      setUploadMethod(e.target.value);
                      setSelectedFile(null);
                      setPasteContent('');
                      setError(null);
                    }}
                    disabled={uploading}
                  />
                  <span>Upload File</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="paste"
                    checked={uploadMethod === 'paste'}
                    onChange={(e) => {
                      setUploadMethod(e.target.value);
                      setSelectedFile(null);
                      setPasteContent('');
                      setError(null);
                    }}
                    disabled={uploading}
                  />
                  <span>Paste Markdown</span>
                </label>
              </div>

              {uploadMethod === 'file' ? (
                <div className="file-upload-section">
                  <input
                    type="file"
                    id="product-detail-file"
                    accept=".md,.txt,text/markdown,text/plain"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                  {selectedFile && (
                    <div className="file-info">
                      <i className="fas fa-file"></i> {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </div>
                  )}
                </div>
              ) : (
                <div className="paste-section">
                  <textarea
                    className="paste-textarea"
                    value={pasteContent}
                    onChange={handlePasteChange}
                    placeholder="Paste or type markdown content here..."
                    disabled={uploading}
                    rows={15}
                  />
                  <div className="paste-info">
                    <span className="char-count">
                      {new TextEncoder().encode(pasteContent).length} / {MAX_FILE_SIZE} bytes
                    </span>
                    {new TextEncoder().encode(pasteContent).length > MAX_FILE_SIZE * 0.8 && (
                      <span className="warning">Approaching size limit</span>
                    )}
                  </div>
                </div>
              )}

              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{uploadProgress}%</span>
                </div>
              )}
            </div>

            <div className="upload-dialog-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setShowUploadDialog(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="upload-submit-button"
                onClick={handleUpload}
                disabled={uploading || (uploadMethod === 'file' && !selectedFile) || (uploadMethod === 'paste' && !pasteContent.trim())}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i> Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Dialog */}
      {showViewDialog && viewingContent && (
        <div className="view-dialog-overlay" onClick={() => setShowViewDialog(false)}>
          <div className="view-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="view-dialog-header">
              <h3>View Document</h3>
              <button
                type="button"
                className="close-button"
                onClick={() => setShowViewDialog(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="view-dialog-tabs">
              <button
                type="button"
                className={`view-tab ${viewTab === 'text' ? 'active' : ''}`}
                onClick={() => setViewTab('text')}
              >
                <i className="fas fa-code"></i> Raw Text
              </button>
              <button
                type="button"
                className={`view-tab ${viewTab === 'formatted' ? 'active' : ''}`}
                onClick={() => setViewTab('formatted')}
              >
                <i className="fas fa-eye"></i> Formatted
              </button>
            </div>
            <div className="view-dialog-content">
              {viewTab === 'text' ? (
                <pre className="markdown-content">{viewingContent}</pre>
              ) : (
                <div 
                  className="markdown-formatted"
                  dangerouslySetInnerHTML={{ __html: markdownToHTML(viewingContent) }}
                />
              )}
            </div>
            <div className="view-dialog-actions">
              <button
                type="button"
                className="close-button"
                onClick={() => setShowViewDialog(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
